import { useCallback, useEffect, useRef, useState } from 'react';
import { useMutation, useQuery } from 'convex/react';
import { api } from '../../../convex/_generated/api'; // DEEP_IMPORT_JUSTIFIED: components in insurance/ need convex api
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { FileText, Download, Plus, File, ImageIcon, ZoomIn } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IPolicyDocumentsSectionProps, IPolicyDocument } from '@/@types/insurance';
import type { Id } from '../../../convex/_generated/dataModel'; // DEEP_IMPORT_JUSTIFIED: convex _generated is the documented exception

function getFileExtension(fileName: string): string {
  const parts = fileName.split('.');
  return parts.length > 1 ? (parts.pop() ?? '').toLowerCase() : '';
}

function isImageFile(fileName: string): boolean {
  const ext = getFileExtension(fileName);
  return ['jpg', 'jpeg', 'png', 'webp', 'gif', 'bmp'].includes(ext);
}

function isPdfFile(fileName: string): boolean {
  return getFileExtension(fileName) === 'pdf';
}

function PdfThumbnail({ url }: { url: string }) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function render() {
      try {
        const pdfjsLib = await import('pdfjs-dist');
        pdfjsLib.GlobalWorkerOptions.workerSrc = new URL(
          'pdfjs-dist/build/pdf.worker.min.mjs',
          import.meta.url
        ).toString();

        const pdf = await pdfjsLib.getDocument(url).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 0.5 });

        const canvas = canvasRef.current;
        if (!canvas || cancelled) return;

        const context = canvas.getContext('2d');
        if (!context) return;

        canvas.width = viewport.width;
        canvas.height = viewport.height;

        await page.render({ canvasContext: context, viewport }).promise;
        if (!cancelled) setLoaded(true);
      } catch {
        // PDF rendering failed — will show fallback icon
      }
    }

    void render();
    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!loaded) {
    return (
      <div className="w-full h-full flex items-center justify-center bg-muted/30">
        <FileText className="w-8 h-8 text-muted-foreground" />
      </div>
    );
  }

  return <canvas ref={canvasRef} className="w-full h-full object-contain" />;
}

function DocumentCard({ storageId, fileName }: { storageId: Id<'_storage'>; fileName: string }) {
  const fileUrl = useQuery(api.storage.getUrl, { storageId });
  const [previewOpen, setPreviewOpen] = useState(false);

  if (fileUrl === undefined) {
    return <Skeleton className="h-40 w-full rounded-lg" />;
  }

  const isImage = isImageFile(fileName);
  const isPdf = isPdfFile(fileName);
  const canPreview = !!fileUrl && (isImage || isPdf);

  return (
    <>
      <div className="group relative flex flex-col rounded-lg border border-border overflow-hidden bg-card hover:border-primary/50 transition-colors">
        {/* Thumbnail */}
        <div
          className={cn(
            'h-28 overflow-hidden bg-muted/20 flex items-center justify-center relative',
            canPreview && 'cursor-pointer'
          )}
          role={canPreview ? 'button' : undefined}
          tabIndex={canPreview ? 0 : undefined}
          aria-label={canPreview ? `Preview ${fileName}` : undefined}
          onClick={canPreview ? () => setPreviewOpen(true) : undefined}
          onKeyDown={
            canPreview
              ? e => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setPreviewOpen(true);
                  }
                }
              : undefined
          }
        >
          {fileUrl && isImage && (
            <img src={fileUrl} alt={fileName} className="w-full h-full object-cover" />
          )}
          {fileUrl && isPdf && <PdfThumbnail url={fileUrl} />}
          {!isImage && !isPdf && <File className="w-8 h-8 text-muted-foreground" />}
          {!fileUrl && <ImageIcon className="w-8 h-8 text-muted-foreground" />}
          {canPreview && (
            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center pointer-events-none">
              <ZoomIn
                className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity"
                aria-hidden="true"
              />
            </div>
          )}
        </div>

        {/* File info + actions */}
        <div className="px-3 py-2 flex items-center justify-between gap-1 min-w-0">
          <span className="text-xs text-foreground truncate flex-1" title={fileName}>
            {fileName}
          </span>
          {fileUrl && (
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0 shrink-0" asChild>
              <a href={fileUrl} download={fileName}>
                <Download className="w-3.5 h-3.5" />
                <span className="sr-only">Download {fileName}</span>
              </a>
            </Button>
          )}
        </div>
      </div>

      {/* Full-size preview dialog */}
      <Dialog open={previewOpen} onOpenChange={setPreviewOpen}>
        <DialogContent className="max-w-4xl w-[90vw] p-0 overflow-hidden">
          <DialogTitle className="sr-only">{fileName}</DialogTitle>
          {isImage && fileUrl && (
            <img
              src={fileUrl}
              alt={fileName}
              className="w-full h-auto max-h-[85vh] object-contain"
            />
          )}
          {isPdf && fileUrl && (
            <iframe
              src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=1`}
              title={fileName}
              className="w-full h-[85vh] border-0"
            />
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PolicyDocumentsSection({
  policyDocuments,
  sourceFileIds,
  policyId,
  orgId: _orgId,
}: IPolicyDocumentsSectionProps) {
  const [isUploading, setIsUploading] = useState(false);

  const createUploadUrl = useMutation(api.storage.createUploadUrl);
  const updatePolicy = useMutation(api['insurance/policies'].update);

  // Merge policyDocuments + legacy sourceFileIds into a unified list.
  // Memoized so handleUpload's dep array doesn't change on every render.
  const allDocuments = useMemo<IPolicyDocument[]>(
    () => [
      ...(policyDocuments ?? []),
      ...sourceFileIds
        .filter(id => !policyDocuments?.some(d => d.storageId === id))
        .map((storageId, i) => ({ storageId, fileName: `Untitled document ${i + 1}` })),
    ],
    [policyDocuments, sourceFileIds]
  );

  const handleUpload = useCallback(async () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.multiple = true;
    input.onchange = async () => {
      const files = input.files;
      if (!files || files.length === 0) return;

      setIsUploading(true);
      try {
        const newDocs: IPolicyDocument[] = [];

        for (const file of Array.from(files)) {
          const uploadUrl = await createUploadUrl({});
          const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: { 'Content-Type': file.type },
            body: file,
          });
          const { storageId } = (await response.json()) as { storageId: Id<'_storage'> }; // AS_JUSTIFIED: Convex upload response is always { storageId: Id<'_storage'> }
          newDocs.push({ storageId, fileName: file.name });
        }

        const updatedDocs = [...allDocuments, ...newDocs];
        await updatePolicy({
          policyId,
          policyDocuments: updatedDocs,
        });
      } finally {
        setIsUploading(false);
      }
    };
    input.click();
  }, [createUploadUrl, updatePolicy, policyId, allDocuments]);

  if (allDocuments.length === 0 && !isUploading) {
    return (
      <Card className="border border-border">
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="w-5 h-5 text-blue-600" />
            Policy Documents
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Button variant="outline" onClick={handleUpload} disabled={isUploading}>
            <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
            Add Document
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border border-border">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="w-5 h-5 text-blue-600" />
            Policy Documents
            <span className="text-xs font-normal text-muted-foreground">
              ({allDocuments.length} file{allDocuments.length !== 1 ? 's' : ''})
            </span>
          </CardTitle>
          <Button variant="outline" size="sm" onClick={handleUpload} disabled={isUploading}>
            <Plus aria-hidden="true" className="w-4 h-4 mr-2" />
            {isUploading ? 'Uploading...' : 'Add Document'}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3">
          {allDocuments.map(doc => (
            <DocumentCard
              key={doc.storageId}
              storageId={doc.storageId as Id<'_storage'>} // AS_JUSTIFIED: storageId comes from typed policyDocuments/sourceFileIds arrays
              fileName={doc.fileName}
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}
